import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import type { AccountInfo } from "@azure/msal-browser";

/**
 * Profile fields we keep from Microsoft Graph `/me`. Mirrors the
 * `$select` projection issued below so unused properties don't bloat
 * the cache or hang on stale schema.
 */
export interface GraphMeProfile {
  displayName: string;
  mail: string;
  userPrincipalName: string;
  givenName: string;
  surname: string;
  jobTitle: string;
}

const CACHE_TTL_MS = 60 * 60 * 1000;          // 1h cache for SUCCESS
const FAILURE_TTL_MS = 60 * 60 * 1000;        // 1h cache for FAILURE (no consent / token endpoint 400)
const SCOPES = ["User.Read"];
const GRAPH_ME_URL =
  "https://graph.microsoft.com/v1.0/me?$select=displayName,mail,userPrincipalName,givenName,surname,jobTitle";

function successKey(account: AccountInfo) {
  return `graph-me:${account.homeAccountId || account.localAccountId}`;
}
function failureKey(account: AccountInfo) {
  return `graph-me-failed:${account.homeAccountId || account.localAccountId}`;
}

function readCached(account: AccountInfo): GraphMeProfile | null {
  try {
    const raw = sessionStorage.getItem(successKey(account));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at: number; profile: GraphMeProfile };
    if (!parsed?.at || Date.now() - parsed.at > CACHE_TTL_MS) return null;
    return parsed.profile;
  } catch {
    return null;
  }
}

function writeCached(account: AccountInfo, profile: GraphMeProfile) {
  try {
    sessionStorage.setItem(
      successKey(account),
      JSON.stringify({ at: Date.now(), profile }),
    );
  } catch {
    /* sessionStorage may be full or blocked — non-fatal */
  }
}

function readFailure(account: AccountInfo): boolean {
  try {
    const raw = sessionStorage.getItem(failureKey(account));
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { at: number };
    if (!parsed?.at || Date.now() - parsed.at > FAILURE_TTL_MS) return false;
    return true;
  } catch {
    return false;
  }
}

function writeFailure(account: AccountInfo) {
  try {
    sessionStorage.setItem(
      failureKey(account),
      JSON.stringify({ at: Date.now() }),
    );
  } catch {
    /* non-fatal */
  }
}

/**
 * Does the account already carry a usable display name in its
 * id_token claims (or the MSAL `account.name` field)? If yes, the
 * AppShell cascade already has what it needs and we can skip the
 * Graph round-trip entirely.
 *
 * Returning `true` means "Graph wouldn't add anything" — used by the
 * hook as an early-exit so we don't fire a token request that's
 * destined to be wasted (or, when the tenant strips User.Read consent,
 * destined to 400 in the browser network log on every navigation).
 */
function alreadyHasIdentity(account: AccountInfo): boolean {
  if (account.name && account.name.trim()) return true;
  const claims = (account.idTokenClaims ?? {}) as Record<string, unknown>;
  const has = (k: string) =>
    typeof claims[k] === "string" && (claims[k] as string).trim().length > 0;
  return has("name") || has("given_name") || has("family_name") ||
         has("preferred_username") || has("upn") || has("email");
}

/**
 * useGraphMe — last-resort identity source for tenants whose id_tokens
 * strip `name` / `preferred_username` (Teams SSO B2B guest configuration).
 *
 * Lifecycle (v3.0.72):
 *
 *   1. If the cached success blob is fresh (1h TTL) — return it
 *      immediately, no Graph call.
 *   2. If the id_token already carries name/given_name/preferred_username/
 *      upn/email — skip Graph entirely. The AppShell cascade has what
 *      it needs from claims; Graph would be pure noise.
 *   3. If the cached failure flag is fresh (1h TTL) — skip Graph. This
 *      kills the 400 noise in the browser network panel after the first
 *      failed attempt per session. The user still sees the id_token /
 *      derived-from-email fallback names in the sidebar.
 *   4. Otherwise — silent `acquireTokenSilent({scopes:["User.Read"]})`
 *      + GET /me, cache success or failure accordingly.
 *
 * We never trigger an interactive popup (consent prompt for a sidebar
 * tooltip is hostile UX). To enable the silent path tenant-wide, an
 * admin should grant consent for User.Read on the app registration:
 *   Azure AD → Enterprise Apps → 6d3d4c7e-e55c-4771-8498-f1c7370db1c6
 *   → Permissions → Grant admin consent for Altelix → User.Read.
 */
export function useGraphMe(): GraphMeProfile | null {
  const { instance } = useMsal();
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];

  const [profile, setProfile] = useState<GraphMeProfile | null>(() =>
    account ? readCached(account) : null,
  );

  useEffect(() => {
    if (!account) return;
    if (profile) return;                  // Already have it (cache or earlier fetch)
    if (alreadyHasIdentity(account)) return; // Cascade has enough — skip Graph
    if (readFailure(account)) return;     // Silent path is known-broken this session
    let cancelled = false;
    (async () => {
      try {
        const tokenResp = await instance.acquireTokenSilent({
          scopes: SCOPES,
          account,
        });
        const res = await fetch(GRAPH_ME_URL, {
          headers: { Authorization: `Bearer ${tokenResp.accessToken}` },
        });
        if (!res.ok) {
          writeFailure(account);
          return;
        }
        const json = (await res.json()) as Partial<GraphMeProfile>;
        const fetched: GraphMeProfile = {
          displayName: (json.displayName ?? "").trim(),
          mail: (json.mail ?? "").trim(),
          userPrincipalName: (json.userPrincipalName ?? "").trim(),
          givenName: (json.givenName ?? "").trim(),
          surname: (json.surname ?? "").trim(),
          jobTitle: (json.jobTitle ?? "").trim(),
        };
        if (cancelled) return;
        writeCached(account, fetched);
        setProfile(fetched);
      } catch {
        // Silent — most commonly InteractionRequiredAuthError when
        // User.Read isn't admin-consented in the tenant. Record the
        // failure so we don't retry on every navigation; the
        // id_token / derived-from-email cascade in AppShell takes
        // over from here.
        writeFailure(account);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [account, instance, profile]);

  return profile;
}
